import React, { useMemo, useState } from "react";
import { Check, X, Sparkles, ArrowRight, Leaf, Waves } from "lucide-react";
import { Link } from "react-router-dom";
import { Slider } from "../components/ui/slider";
import Reveal from "../components/site/Reveal";
import FaqSection from "../components/site/FaqSection";
import { PageHero, SectionTitle, CtaLink, Amp } from "../components/site/Common";
import { OrganicDot } from "../components/site/Logo";
import { PLANS } from "../mock";
import { PRICE_CITIES, pricePath } from "../seoPages";

const fmt = (n) => n.toLocaleString("fr-FR") + " €";

const COMPARE = [
  ["Création & optimisation d'annonce", true, true, true],
  ["Diffusion Airbnb, Booking, Abritel", true, true, true],
  ["Prix dynamiques quotidiens", true, true, true],
  ["Messagerie voyageurs 7j/7, astreinte jusqu'à 23h", true, true, true],
  ["Service propriétaire 7j/7, de 8h à 23h", false, false, true],
  ["Shooting photo professionnel", false, true, true],
  ["Accueil & remise des clés", false, true, true],
  ["Ménage après chaque séjour (payé par les voyageurs)", true, true, true],
  ["Linge de maison inclus", "Option voyageur", true, true],
  ["Petite maintenance", false, true, true],
  ["Interlocuteur unique dédié", false, false, true],
  ["Conciergerie voyageurs premium", false, false, true],
  ["Jardinage (dès 35 € / passage)", "En option", "En option", "En option"],
  ["Entretien de la piscine (dès 45 € / passage)", "En option", "En option", "En option"],
];

const OPTIONS = [
  [Leaf, "Jardinage", "Tonte, taille des haies, désherbage et arrosage pour un extérieur toujours accueillant, en saison ou toute l'année.", "À partir de 35 € par passage", "/options/jardinage"],
  [Waves, "Entretien de la piscine", "Contrôle de l'eau, produits, nettoyage du bassin et des filtres, ouverture et hivernage de la piscine.", "À partir de 45 € par passage", "/options/entretien-piscine"],
];

function OptionsSection() {
  return (
    <section className="pb-24" data-testid="options-section">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <SectionTitle center kicker="Options à la carte" title="en plus" script="de votre formule" text="Disponibles avec toutes les formules, facturées en supplément. Le prix final dépend de la taille du jardin ou de la piscine." />
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {OPTIONS.map(([Icon, t, d, price, to], i) => (
            <Reveal key={t} delay={i * 100}>
              <Link to={to} data-testid={`option-card-${i}`} className="group h-full rounded-[28px] bg-white ring-1 ring-[#e6dcc8] hover:ring-[#d9895a] hover:-translate-y-1 transition-[transform,box-shadow] duration-300 hover:shadow-[0_30px_60px_-30px_rgba(19,38,28,0.35)] p-8 flex gap-5">
                <span className="w-14 h-14 rounded-2xl bg-[#f6e3d6] grid place-items-center shrink-0"><Icon className="w-7 h-7 text-[#c4703f]" /></span>
                <div>
                  <p className="font-logo font-black text-xl text-[#13261c]">{t}</p>
                  <p className="mt-2 text-[#5a6158] leading-relaxed">{d}</p>
                  <p className="mt-4 font-logo font-black text-lg lg:text-2xl text-[#c4703f]" data-testid={`option-price-${i}`}>{price}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#13261c] group-hover:text-[#c4703f]">En savoir plus <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const SOLO = [
  ["Annonce et photos", "À rédiger et photographier vous-même", "Annonce optimisée en français et en anglais, shooting offert (Sérénité, Premium)"],
  ["Prix", "Fixés à la main, souvent trop bas en été et trop hauts hors saison", "Ajustés chaque nuit selon la saison, les week-ends et les événements"],
  ["Messages voyageurs", "Vous répondez à chaque demande, même en vacances", "Nous répondons pour vous, en français et en anglais"],
  ["Arrivées et départs", "Vous devez être là ou trouver quelqu'un", "Boîte à clés ou accueil en personne, de 16h à 20h"],
  ["Ménage et linge", "À organiser entre chaque séjour", "Ménage et linge préparés à chaque départ"],
  ["Imprévus", "Fuite, clés perdues, panne : c'est pour vous", "Astreinte urgences et artisans locaux de confiance"],
  ["Plateformes", "Une seule annonce, souvent sur Airbnb uniquement", "Airbnb, Booking et Vrbo, calendriers synchronisés"],
  ["Votre temps", "Plusieurs heures chaque semaine", "Vous suivez tout depuis votre Espace propriétaire"],
];

function SoloCompare() {
  return (
    <section className="py-24 bg-[#13261c] grain relative overflow-hidden" data-testid="solo-compare">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 relative">
        <SectionTitle light center kicker="Louer seul ou avec nous" title="ce qui change" script="au quotidien" />
        <Reveal className="mt-12 rounded-[28px] overflow-hidden ring-1 ring-white/15">
          <div className="hidden md:grid grid-cols-[1fr_1.4fr_1.4fr] bg-white/5 text-sm font-bold uppercase tracking-[0.14em]">
            <span className="p-5 text-[#faf7f0]/60" /> <span className="p-5 text-[#faf7f0]/60">Seul</span><span className="p-5 text-[#d9895a]">Avec Ouest Conciergerie</span>
          </div>
          {SOLO.map(([k, a, b]) => (
            <div key={k} className="grid md:grid-cols-[1fr_1.4fr_1.4fr] border-t border-white/10 text-[15px]" data-testid={`solo-row-${SOLO.findIndex((x) => x[0] === k)}`}>
              <span className="p-5 pb-1 md:pb-5 font-bold text-white">{k}</span>
              <span className="px-5 py-1 md:p-5 flex gap-2 text-[#faf7f0]/60"><X className="w-4 h-4 mt-1 shrink-0 text-[#faf7f0]/35" />{a}</span>
              <span className="px-5 pt-1 pb-5 md:p-5 flex gap-2 text-white md:bg-white/5"><Check className="w-4 h-4 mt-1 shrink-0 text-[#d9895a]" />{b}</span>
            </div>
          ))}
        </Reveal>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <CtaLink to="/rendez-vous" variant="yellow" testid="solo-rdv">Prendre rendez-vous</CtaLink>
          <CtaLink to="/proprietaires" variant="outline" testid="solo-sim">Simuler mes revenus</CtaLink>
        </div>
      </div>
    </section>
  );
}

export default function Tarifs() {
  const [plan, setPlan] = useState("serenite");
  const [gross, setGross] = useState([22000]);
  const p = PLANS.find((x) => x.id === plan);
  const res = useMemo(() => {
    const fee = Math.round(gross[0] * p.rate / 100);
    return { fee, net: gross[0] - fee };
  }, [gross, p]);

  return (
    <div data-testid="tarifs-page">
      <PageHero kicker="Nos tarifs" title="une commission simple" script="zéro frais caché" text="Vous ne payez que si vous gagnez. Pas de frais d'entrée, pas d'abonnement, ménage payé par les voyageurs." />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((pl, i) => (
            <Reveal key={pl.id} delay={i * 100}
              className={`relative rounded-[32px] p-8 lg:p-10 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${pl.popular ? "bg-[#1c3426] text-white shadow-[0_40px_80px_-30px_rgba(19,38,28,0.7)]" : "bg-white ring-1 ring-[#e6dcc8] hover:shadow-[0_30px_60px_-35px_rgba(19,38,28,0.4)]"}`}>
              <div data-testid={`plan-${pl.id}`} className="flex flex-col flex-1">
                {pl.popular && <span className="absolute -top-4 left-8 inline-flex items-center gap-1.5 rounded-full bg-[#d9895a] text-[#13261c] text-xs font-extrabold uppercase tracking-wider px-4 py-2"><Sparkles className="w-3.5 h-3.5" />Le plus choisi</span>}
                <h3 className={`font-logo font-black tracking-[-0.025em] text-3xl lowercase ${pl.popular ? "text-white" : "text-[#13261c]"}`}>{pl.name}<OrganicDot className="inline-block w-2 h-2 ml-0.5 align-baseline" /></h3>
                <p className="mt-1 font-script text-[#d9895a] text-2xl"><Amp text={pl.tagline} /></p>
                <p className="mt-8 flex items-end gap-2"><span className="font-logo font-black tracking-[-0.03em] text-7xl">{pl.rate}%</span><span className={`pb-3 text-sm font-semibold ${pl.popular ? "text-[#faf7f0]/85" : "text-[#6d7268]"}`}>des revenus<br />TTC</span></p>
                <ul className="mt-8 space-y-3 flex-1">
                  {pl.features.map((f) => <li key={f} className="flex gap-3"><Check className={`w-5 h-5 shrink-0 ${pl.popular ? "text-[#d9895a]" : "text-[#c4703f]"}`} />{f}</li>)}
                  {pl.missing.map((f) => <li key={f} className={`flex gap-3 line-through ${pl.popular ? "text-[#faf7f0]/50" : "text-[#b3ab98]"}`}><X className="w-5 h-5 shrink-0" />{f}</li>)}
                </ul>
                {pl.note && <p className="mt-6 rounded-2xl bg-[#f3ede1] text-[#5a6158] text-sm p-4" data-testid={`plan-note-${pl.id}`}>{pl.note}</p>}
                <Link to={`/demarrer/${pl.id}`} data-testid={`plan-cta-${pl.id}`}
                  className={`mt-10 text-center rounded-full py-4 font-bold transition-colors ${pl.popular ? "bg-[#d9895a] text-[#13261c] hover:bg-[#e39d72]" : "bg-[#f1ebdd] text-[#1c3426] hover:bg-[#13261c] hover:text-white"}`}>
                  Choisir {pl.name}
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="text-center text-sm text-[#6d7268] mt-8 px-5 leading-relaxed" data-testid="tarifs-menage-note">* Quelle que soit la formule, le ménage est toujours payé en plus par les voyageurs, jamais par vous. <br />Shooting photo offert pour toute signature avant le 31 décembre.</p>
        <p className="text-center text-sm text-[#6d7268] mt-4 px-5" data-testid="tarifs-city-prices">Prix par ville : {PRICE_CITIES.map((n, i) => <React.Fragment key={n}>{i ? " · " : ""}<Link to={pricePath(n)} className="underline hover:text-[#13261c]">{n}</Link></React.Fragment>)}</p>
      </section>

      <section className="py-24 bg-[#efe7d6]" data-testid="simulator-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <SectionTitle kicker="Simulateur" title="ce qu'il vous reste" script="concrètement" text="Faites varier vos revenus annuels bruts et la formule pour voir votre revenu net après commission." />
          <Reveal delay={100} className="bg-white rounded-[32px] p-8 lg:p-10 shadow-[0_40px_80px_-40px_rgba(19,38,28,0.45)]">
            <div className="flex gap-2 bg-[#f3ede1] p-1.5 rounded-full">
              {PLANS.map((pl) => (
                <button key={pl.id} onClick={() => setPlan(pl.id)} data-testid={`sim-plan-${pl.id}`}
                  className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-colors ${plan === pl.id ? "bg-[#13261c] text-white" : "text-[#2c3a31] hover:bg-white"}`}>{pl.name}</button>
              ))}
            </div>
            <div className="mt-8 flex justify-between items-end"><p className="font-semibold text-[#5a6158]">Revenus bruts annuels</p><p className="text-2xl font-extrabold text-[#13261c]" data-testid="sim-gross">{fmt(gross[0])}</p></div>
            <Slider value={gross} onValueChange={setGross} min={5000} max={80000} step={500} className="mt-5" data-testid="sim-slider" />
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="rounded-2xl bg-[#f3ede1] p-5"><p className="text-sm text-[#6d7268] font-semibold">Commission ({p.rate}%)</p><p className="text-xl font-extrabold text-[#13261c] mt-1" data-testid="sim-fee">{fmt(res.fee)}</p></div>
              <div className="rounded-2xl bg-[#13261c] p-5"><p className="text-sm text-[#faf7f0] font-semibold">Votre revenu net</p><p className="text-xl font-extrabold text-white mt-1" data-testid="sim-net">{fmt(res.net)}</p></div>
            </div>
            <div className="mt-6"><CtaLink to="/proprietaires" testid="sim-cta">Simuler mon logement</CtaLink></div>
          </Reveal>
        </div>
      </section>

      <section className="py-24" data-testid="compare-section">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <SectionTitle center kicker="Comparatif" title="le détail" script="des formules" />
          <Reveal className="mt-12 overflow-x-auto rounded-[28px] ring-1 ring-[#e6dcc8]">
            <table className="w-full min-w-[600px] text-left">
              <thead><tr className="bg-[#f3ede1]"><th className="p-5 font-bold text-[#13261c]">Services</th>{PLANS.map((pl) => <th key={pl.id} className="p-5 text-center font-bold text-[#13261c]">{pl.name}<span className="block text-sm text-[#c4703f]">{pl.rate}%</span></th>)}</tr></thead>
              <tbody>
                {COMPARE.map(([label, ...vals]) => (
                  <tr key={label} className="border-t border-[#f1ebdd] hover:bg-[#fcfaf5] transition-colors">
                    <td className="p-5 text-[#2c3a31] font-medium">{label}</td>
                    {vals.map((v, k) => <td key={k} className="p-5 text-center">{typeof v === "string" ? <span className="text-xs font-bold text-[#c4703f]">{v}</span> : v ? <Check className="w-5 h-5 text-[#c4703f] mx-auto" /> : <X className="w-5 h-5 text-[#d6d0c2] mx-auto" />}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>
      <OptionsSection />
      <SoloCompare />
      <FaqSection />
    </div>
  );
}
